import {
    ArticlePreviewResponse,
    ArticleSearchResultResponse,
    CollectionRuleResponse,
    PrivateAboutPagesResponse,
    ProfileCollectionLayout,
    PublicAboutPageResponse,
    UserResponse,
    CollectionRuleType,
    ProfileView,
    UserCoverStyle,
    UserProfileStyle,
    ProfileUserResponse
} from "@authory-packages/core";
import { useFormik } from "formik";
import { OGTags, SEO_TYPE } from "../../components/OGTags";
import { UnstyledLink } from "../../components/ReusableStyledComponents/UnstyledLink";
import { V3BodyGrey35 } from "../../components/ReusableStyledComponents/V3BodyGrey35";
import { APPLICATION_ROUTES } from "../../shared-logic/routes";
import { V4ProfileHero } from "../../components/ProfileHero";
import { ContactUserFormik } from "../../components/V3ContactUser";
import { ProfileFilters } from "../../components/ProfileFilters";
import { VerticalSpacing } from "../../components/VerticalSpacing";
import { V3NoProfileCollections, V3NoSearchResults, V3ProfileEmptyCollection } from "../../components/V3EmptyStateScreen";
import { processQueryArgs } from "../../utils/processQueryArgs";
import { V3ProfileList, V3ProfileListProps } from "../../components/V3ProfileList";
import { InfinitePaginationFooter } from "../../components/InfinitePaginationFooter";
import {
    PCFWrapper,
    ProfileHeroEditConverWrapper,
    PublicProfileSearchItemsContentSizer,
    PublicProfileSearchItemsPad,
    SidebarLayoutTitle,
    V3PublicPageTeaser,
    V4EditCoverPos
} from "./V4ProfilePublicPageLayout.styles";
import { EditableSortableType } from "../../components/EditableSortableItems/EditableSortableItems.types";
import { ReadOnlySortableItems } from "../../components/EditableSortableItems/ReadOnlySortableItems";
import { UpdateProfileFormik } from "../../components/V3EditProfilemodal";
import { DEFAULT_SEARCH_PARAMS } from "../../types/profile_search_params";
import { DEFAULT_COLLECTION_LABEL } from "../../types/collection";
import { useContext, useState, type ElementType, type ReactNode } from "react";
import { SourceInProgress } from "../../components/SourceInProgress";
import { V3TertiaryButton } from "../../components/Button";
import { V3IconBin, V3IconImage } from "../../components/Icons";
import { AuthoryColorSchemes } from "../../components/ProfileHero/V3ProfileHero.types";
import { SidebarLayoutShowDesktop } from "../../layout/SideBarLayout/SidebarLayout.styles";
import { CollectionBannerDetails } from "../../components/CollectionBannerDetails";
import { NoNameBanner } from "../../components/NoNameBanner";
import { showCollectionAutoUpdateBanner, showCollectionNoNameBanner } from "../../utils/collectionBanners";
import { showAboutPageNoNameBanner } from "../../utils/aboutPageBanners";
import { V3BlueRoundPlus } from "../../components/ReusableStyledComponents/V3BlueRoundPlus";
import { V4AboutPageContent } from "../../components/V4AboutPageContent";
import React from "react";
import { FadeInContainer } from "../../components/ReusableStyledComponents/FadeIn";
import { PublicCollectionList } from "../../components/CollectionList/PublicCollectionList";
import { ContentGridWithFooter } from "../../components/ContentGridWithFooter";
import { GenericContentEditable } from "../../components/GenericContentEditable";
import { ClientOnly } from "../../components/ClientOnly";
import { useTheme } from "styled-components";
import { MediaEditingFormik } from "../../components/V3MediaEditingModal";
import { V3TertiaryCTAColor } from "../../components/Button/V3TertiaryCTA.types";
import { AboutPagesTitle } from "../../components/ProfileHero/V4ProfileHero.styles";
import { ItemUrlContext } from "../../components/V3ProfileList/V3ProfileList.utils";
import { getProfileUrl } from "../../utils/getAboutPagesWithHref";
import { DomainContext } from "../../context/domainContext";
import { RequestHost } from "../../types/headers";
import { isMainDomain } from "../../utils/domainRouting";
import dynamic from "next/dynamic";

const EditableSortableItems = dynamic(() =>
    import("../../components/EditableSortableItems/EditableSortableItems").then((m) => m.EditableSortableItems)
);
const EditableIt = dynamic(() => import("../../components/EditableSortableItems/EditableSortableItems").then((m) => m.EditableIt));
const CollectionAddItemDropdown = dynamic(() =>
    import("../../components/EditableSortableItems/CollectionAddItemDropdown").then((m) => m.CollectionAddItemDropdown)
);
const CollectionList = dynamic(() => import("../../components/CollectionList/CollectionList").then((m) => m.CollectionList));
const CreateUpdateCollectionModal = dynamic(() =>
    import("../../components/CreateUpdateCollectionModal/CreateUpdateCollectionModal").then((m) => m.CreateUpdateCollectionModal)
);
const V3ModalSubscribed = dynamic(() => import("../../components/V3ModalSubscribed/V3ModalSubscribed").then((m) => m.V3ModalSubscribed));
const V3ModalUnsubscribed = dynamic(() => import("../../components/V3ModalUnsubscribed/V3ModalUnsubscribed").then((m) => m.V3ModalUnsubscribed));

export type ProfileSearchForm = {
    collection: string;
    text: string;
    source: string;
    subAccount: string;
    type: string;
    aboutPage?: string;
};

export interface V4ProfilePublicPageLayoutProps {
    isPrivateView?: boolean;
    visitorBannerOpen?: boolean;
    author: UserResponse | ProfileUserResponse;
    queryArgs: Record<string, string>;
    subscribeOpen?: boolean;
    items: ArticleSearchResultResponse[];
    itemsLoading: boolean;
    customDomainSearchTriggered?: boolean;
    contentCanFetchmore: boolean;
    subscribedModalOpen?: boolean;
    unsubscribedModalOpen?: boolean;
    initialContactOpen?: boolean;
    rules: CollectionRuleResponse[];
    signupSkeleton?: boolean;
    skipCookieYes?: boolean;
    heroWrapper?: ElementType<{ children: ReactNode }>;
    mainWrapper?: ElementType<{ children: ReactNode }>;
    userAboutPage?: PublicAboutPageResponse | PrivateAboutPagesResponse;
    isAboutPage?: boolean;
    smartShareUrl?: string;
    aboutPageLoading?: boolean;
    disableInteraction?: boolean;
    contentLoadmore: () => void;
    onSocialsEditableClick?: () => void;
    onContactEditableClick?: () => void;
    onCoverEditableClick?: () => void;
    onPIEditableClick?: () => void;
    onProfileFilterEditableClick?: () => void;
    onEditProfileModalSubmit?: (data: UpdateProfileFormik) => void;
    onSearchFormSubmit?: (e: ProfileSearchForm) => void;
    onCoverModalsubmit?: (data: MediaEditingFormik) => Promise<void>;
    onCollectionStopDrag?: (data: Record<string, number>) => void;
    onCollectionSubmitName?: (name: string, slug?: string) => Promise<any>;
    onCollectionAddToProfile?: (slug: string) => void;
    onCollectionRemoveFromProfile?: (slug: string) => void;
    onCollectionUpdateRules?: (slug: string) => void;
    onAboutPageSubmitName?: (name: string, slug?: string) => Promise<any>;
    onAboutPageStopDrag?: (data: Record<string, number>) => void;
    onAboutPageDelete?: (id: string) => void;
    onAboutPageSubmitTitle?: (title: string, id: string) => Promise<any>;
    onTemporaryImageUploadHandler?: (file: File) => Promise<string>;
    onAboutPageSubmitContent?: (html: string, id: string) => Promise<void>;
    onContactUserSubmit: (data: ContactUserFormik) => Promise<void>;
    onSubscribeFormSubmit: (email: string) => Promise<void>;
    onItemClick?: (id: string) => void;
    renderProfileList?: (props: V3ProfileListProps) => ReactNode;
}

export const V4ProfilePublicPageLayout = ({
    author,
    queryArgs,
    isPrivateView = false,
    visitorBannerOpen = false,
    subscribeOpen = false,
    items,
    itemsLoading,
    smartShareUrl,
    subscribedModalOpen = false,
    unsubscribedModalOpen = false,
    initialContactOpen = false,
    disableInteraction = false,
    contentCanFetchmore,
    rules,
    signupSkeleton = false,
    skipCookieYes = false,
    heroWrapper: HeroWrapper = "div",
    mainWrapper: MainWrapper = "div",
    userAboutPage,
    isAboutPage = false,
    contentLoadmore,
    onSocialsEditableClick,
    onContactEditableClick,
    onCoverEditableClick,
    onPIEditableClick,
    onProfileFilterEditableClick,
    onEditProfileModalSubmit,
    onSearchFormSubmit,
    onCollectionStopDrag,
    onCollectionSubmitName,
    onCollectionAddToProfile,
    onCollectionRemoveFromProfile,
    onCollectionUpdateRules,
    onAboutPageSubmitName,
    onAboutPageStopDrag,
    onAboutPageDelete,
    onCoverModalsubmit,
    onTemporaryImageUploadHandler,
    onAboutPageSubmitContent,
    onAboutPageSubmitTitle,
    onSubscribeFormSubmit,
    onContactUserSubmit,
    onItemClick,
    renderProfileList = (props) => <V3ProfileList {...props} />
}: V4ProfilePublicPageLayoutProps) => {
    const { searchArgs, profileCollections } = processQueryArgs(queryArgs, author);
    const [subscribeCompletedModal, setSubscribeCompletedModal] = useState(subscribedModalOpen);
    const [unsubscribedModal, setUnsubscribedModal] = useState(unsubscribedModalOpen);
    const [nameCollectionModalOpen, setNameCollectionModalOpen] = useState(false);
    const [nameAboutPageModalOpen, setNameAboutPageModalOpen] = useState(false);
    const theme = useTheme();
    const requestHostHeader: RequestHost = useContext(DomainContext) ?? RequestHost.MAINDOMAIN;

    const formik = useFormik<ProfileSearchForm>({
        initialValues: {
            collection: searchArgs?.collection || "",
            text: searchArgs?.text || DEFAULT_SEARCH_PARAMS.TEXT,
            source: searchArgs?.source || DEFAULT_SEARCH_PARAMS.SOURCE,
            subAccount: searchArgs?.subAccount || DEFAULT_SEARCH_PARAMS.SUBACCOUNT,
            type: searchArgs?.type || DEFAULT_SEARCH_PARAMS.TYPE,
            aboutPage: searchArgs?.aboutPage || DEFAULT_SEARCH_PARAMS.ABOUTPAGE
        },
        enableReinitialize: true,
        onSubmit: (data) => {
            const formikCollection = author.collections.find((col) => col.canonicalSlug === data?.collection);

            onSearchFormSubmit &&
                onSearchFormSubmit({
                    ...data,
                    collection: formikCollection?.canonicalSlug || data.collection
                });
        }
    });

    const isSidebar = theme.customization.profileStyle === UserProfileStyle.sidebar;
    const hasItems = !!items.reduce((acc, curr) => acc + curr.articles.length, 0);
    const profileHasCollectionsAdded = !!profileCollections.length;
    const hasImage = author.coverStyle === UserCoverStyle.image || author.coverStyle === UserCoverStyle.unsplash;
    const profileHasCollections = !!author.collections.length;
    const collectionTarget = profileCollections.find((col) => col.canonicalSlug === searchArgs?.collection);
    const isTabsCollectionLayout = theme.customization.profileCollectionLayout === ProfileCollectionLayout.tabs;
    const isGridCollectionLayout = theme.customization.profileCollectionLayout === ProfileCollectionLayout.grid;
    const collectionIsEmpty = collectionTarget && !collectionTarget.publicationCount && isTabsCollectionLayout;
    const isEmptyProfile =
        !isAboutPage && isTabsCollectionLayout && !itemsLoading && (!hasItems || !!collectionIsEmpty || !profileHasCollectionsAdded);

    const srcInProgress = (
        collectionTarget?.default
            ? author.sources
            : author.sources.filter((src) =>
                  (rules.find((rule) => rule.type === CollectionRuleType.Source)?.params || []).find((it) => it === src?.canonicalSlug)
              )
    ).filter((src) => src.inProgress);

    // Background and colors
    const showFilters = isPrivateView || author.profileFilterEnabled;
    const showCollections = isPrivateView || profileCollections.length > 1;

    const privateCollections = author.collections.filter((col) => !col.partOfProfile);

    const structuredDataItems =
        isTabsCollectionLayout && profileHasCollectionsAdded && !collectionIsEmpty
            ? [items[0]?.pinned, ...items.flatMap((page) => page.articles)].filter((item): item is ArticlePreviewResponse => !!item)
            : [];
    // Only grid cards link to collection pages, tabs switch the list on this page
    const structuredDataCollections = isGridCollectionLayout ? profileCollections : [];

    const AnimationToUse = isPrivateView ? React.Fragment : FadeInContainer;
    const propsToUse = isPrivateView ? {} : { $customDuration: "600ms" };

    const createCollectionHandler = () => onCollectionSubmitName && onCollectionSubmitName(DEFAULT_COLLECTION_LABEL);

    const aboutPageId = queryArgs.aboutPage || userAboutPage?.slug;

    // important for ref to update on editable component
    const componentKey = queryArgs.aboutPage || author.slug;

    const onAboutPageTitleEnd = (value: string) => {
        onAboutPageSubmitTitle && aboutPageId && onAboutPageSubmitTitle(value, aboutPageId);
    };

    const onProfileBioEditEnd = (html: string) => {
        const data: UpdateProfileFormik = {
            bio: html
        };

        onEditProfileModalSubmit && onEditProfileModalSubmit(data);
    };

    const TitleToUse = isAboutPage ? AboutPagesTitle : SidebarLayoutTitle;

    // Server rendered for visitors so crawlers that run no JavaScript see the bio. The editable is a div, so its title
    // renders as a div too, a p would be closed by the parser and break hydration.
    const sidebarTitle = (
        <GenericContentEditable
            key={componentKey}
            colorVariant={AuthoryColorSchemes.userTheme}
            text={(isAboutPage ? userAboutPage?.title : author.bio) || ""}
            editMode={isPrivateView}
            onEditCallback={(value) => {
                isAboutPage ? onAboutPageTitleEnd(value) : onProfileBioEditEnd(value);
            }}
        />
    );

    // Visitor tabs link to the url a click switches to, so crawlers can follow them; the click itself stays a shallow
    // switch. The editor shares these tabs and must not navigate on click.
    const collectionTabs = profileCollections.map((coll) => ({
        id: coll.canonicalSlug,
        label: coll.label,
        hasRules: !!coll.ruleCount,
        isDefault: coll.default,
        href: isPrivateView ? undefined : `${getProfileUrl(author.slug, isMainDomain(requestHostHeader))}?collection=${coll.canonicalSlug}`
    }));

    /**
     * Switches the listed collection when a collection tab is picked.
     * @param slug The canonical slug of the picked collection
     */
    function onCollectionTabSelect(slug: string) {
        if (slug === formik.values.collection) return;
        formik.setFieldValue("collection", author.collections.find((coll) => coll.canonicalSlug === slug)?.canonicalSlug);
        formik.submitForm();
    }

    return (
        <div>
            {visitorBannerOpen && (
                <V3PublicPageTeaser>
                    <V3BodyGrey35>
                        Hey! You're viewing this profile as a visitor! Why don't you{" "}
                        <UnstyledLink target="_blank" href={APPLICATION_ROUTES.SIGNUP} style={{ textDecoration: "underline" }}>
                            create your own
                        </UnstyledLink>
                        ?
                    </V3BodyGrey35>
                </V3PublicPageTeaser>
            )}
            {author && !isPrivateView && (
                <OGTags
                    user={author}
                    target={isAboutPage ? SEO_TYPE.ABOUT_PAGE : SEO_TYPE.PROFILE}
                    skipCookieYes={skipCookieYes}
                    aboutPage={userAboutPage}
                    context={ItemUrlContext.profile}
                    forceNoIndex={!!smartShareUrl || isEmptyProfile}
                    items={structuredDataItems}
                    collections={structuredDataCollections}
                />
            )}
            <ContentGridWithFooter
                isPrivateView={isPrivateView}
                showAuthoryBranding={author.profileAuthoryBranding}
                useExtendedBreakpoint={isSidebar}
                skipSideBarDesktop={!isSidebar}
                disableInteraction={disableInteraction}
                dynamicContent={
                    <>
                        <HeroWrapper>
                            <ProfileHeroEditConverWrapper>
                                <V4ProfileHero
                                    author={author}
                                    key={componentKey}
                                    onContactUserSubmit={onContactUserSubmit}
                                    editMode={isPrivateView}
                                    onSocialsEditableClick={onSocialsEditableClick}
                                    onContactEditableClick={onContactEditableClick}
                                    onPIEditableClick={onPIEditableClick}
                                    onEditProfileModalSubmit={onEditProfileModalSubmit}
                                    initialContactOpen={initialContactOpen}
                                    onAboutPageSubmitName={onAboutPageSubmitName}
                                    onAboutPageStopDrag={onAboutPageStopDrag}
                                    onAboutPageDelete={onAboutPageDelete}
                                    onSubscribeFormSubmit={onSubscribeFormSubmit}
                                    onAboutPageSubmitTitle={onAboutPageSubmitTitle}
                                    aboutPageTitle={userAboutPage?.title}
                                    smartShareUrl={smartShareUrl}
                                    onAboutPageSelect={(id) => {
                                        if (isPrivateView) {
                                            formik.setFieldValue("aboutPage", id);
                                            formik.submitForm();
                                        }
                                    }}
                                    aboutPageId={queryArgs.aboutPage || userAboutPage?.slug}
                                    isAboutPage={isAboutPage}
                                    subscribeOpen={subscribeOpen}
                                    isHeadlinePageHeading={!isAboutPage && !isPrivateView}
                                />
                                {isPrivateView && !isAboutPage && (
                                    <SidebarLayoutShowDesktop>
                                        <V4EditCoverPos
                                            $viewType={theme.customization.defaultView as ProfileView}
                                            $profileStyle={theme.customization.profileStyle}
                                            $isAboutPage={isAboutPage}
                                            $profileCollectionLayout={theme.customization.profileCollectionLayout}
                                        >
                                            <div>
                                                <div>
                                                    <V3TertiaryButton
                                                        skipSVGColor
                                                        ctaColor={hasImage ? V3TertiaryCTAColor.white : V3TertiaryCTAColor.color}
                                                        onClick={onCoverEditableClick}
                                                        icon={<V3IconImage stroke={hasImage ? theme.v3.colors.white : theme.v3.colors.grey70} />}
                                                        text={author.coverStyle !== UserCoverStyle.none ? "Edit cover" : "Add cover"}
                                                    />
                                                </div>
                                                {author.coverStyle !== UserCoverStyle.none && (
                                                    <div>
                                                        <V3TertiaryButton
                                                            skipSVGColor
                                                            ctaColor={hasImage ? V3TertiaryCTAColor.white : V3TertiaryCTAColor.color}
                                                            onClick={() => {
                                                                onCoverModalsubmit && onCoverModalsubmit({ style: UserCoverStyle.none, color: "" });
                                                            }}
                                                            icon={<V3IconBin stroke={hasImage ? theme.v3.colors.white : theme.v3.colors.grey70} />}
                                                            text="Remove cover"
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </V4EditCoverPos>
                                    </SidebarLayoutShowDesktop>
                                )}
                            </ProfileHeroEditConverWrapper>
                        </HeroWrapper>
                    </>
                }
                mainContent={
                    <>
                        <MainWrapper>
                            <PublicProfileSearchItemsPad>
                                <PublicProfileSearchItemsContentSizer
                                    $viewType={theme.customization.defaultView as ProfileView}
                                    $profileStyle={theme.customization.profileStyle}
                                    $isAboutPage={isAboutPage}
                                    $profileCollectionLayout={theme.customization.profileCollectionLayout}
                                >
                                    {isSidebar && (
                                        <VerticalSpacing
                                            bottom={24}
                                            top={24}
                                            breakpoints={[{ screen: theme.breakpoints.tablet, marginBottom: isAboutPage ? 24 : 48, marginTop: 48 }]}
                                            style={{
                                                marginLeft:
                                                    theme.customization.profileStyle === UserProfileStyle.sidebar && isPrivateView && !isAboutPage
                                                        ? -6
                                                        : 0
                                            }}
                                        >
                                            <TitleToUse as={isAboutPage ? "h1" : "div"}>
                                                {isPrivateView ? <ClientOnly>{sidebarTitle}</ClientOnly> : sidebarTitle}
                                            </TitleToUse>
                                        </VerticalSpacing>
                                    )}
                                    <AnimationToUse {...propsToUse}>
                                        {isAboutPage ? (
                                            <>
                                                {isPrivateView && userAboutPage && showAboutPageNoNameBanner(userAboutPage) && (
                                                    <NoNameBanner
                                                        entity="page"
                                                        name={userAboutPage.name}
                                                        onNameClick={() => setNameAboutPageModalOpen(true)}
                                                    />
                                                )}
                                                <V4AboutPageContent
                                                    isPrivateView={isPrivateView}
                                                    userAboutPage={userAboutPage}
                                                    queryArgs={queryArgs}
                                                    onTemporaryImageUploadHandler={onTemporaryImageUploadHandler}
                                                    onAboutPageSubmitContent={onAboutPageSubmitContent}
                                                />
                                            </>
                                        ) : (
                                            <>
                                                {isTabsCollectionLayout && (
                                                    <VerticalSpacing
                                                        bottom={showCollections || showFilters ? 0 : 12}
                                                        style={{ position: "relative", zIndex: 1 }}
                                                    >
                                                        <PCFWrapper $sideMargins={showCollections || showFilters} $isPrivateView={isPrivateView}>
                                                            {showCollections && !isPrivateView && (
                                                                <div>
                                                                    <ReadOnlySortableItems
                                                                        colorVariant={AuthoryColorSchemes.userTheme}
                                                                        its={collectionTabs}
                                                                        activeCol={formik.values.collection}
                                                                        onItemSelect={onCollectionTabSelect}
                                                                    />
                                                                </div>
                                                            )}
                                                            {showCollections && isPrivateView && (
                                                                <div>
                                                                    <EditableSortableItems
                                                                        colorVariant={AuthoryColorSchemes.userTheme}
                                                                        its={collectionTabs}
                                                                        editMode
                                                                        activeCol={formik.values.collection}
                                                                        onItemSelect={onCollectionTabSelect}
                                                                        onCollectionStopDrag={onCollectionStopDrag}
                                                                        onCollectionSubmitName={onCollectionSubmitName}
                                                                        onCollectionRemoveFromProfile={onCollectionRemoveFromProfile}
                                                                        onCollectionUpdateRules={onCollectionUpdateRules}
                                                                        renderShortcutAddItem={(scrolling, onOpenChange, simplebarScroll) => {
                                                                            return (
                                                                                <CollectionAddItemDropdown
                                                                                    customOffset={{ crossAxis: -6, mainAxis: 0 }}
                                                                                    onNew={createCollectionHandler}
                                                                                    onCollectionAddToProfile={onCollectionAddToProfile}
                                                                                    collections={privateCollections}
                                                                                    scrolling={scrolling}
                                                                                    onOpenChange={onOpenChange}
                                                                                    scrollSimpleBar={simplebarScroll}
                                                                                >
                                                                                    <div style={{ padding: 18, display: "flex" }}>
                                                                                        <V3BlueRoundPlus />
                                                                                    </div>
                                                                                </CollectionAddItemDropdown>
                                                                            );
                                                                        }}
                                                                        renderMainAddItem={(
                                                                            scrolling,
                                                                            onOpenChange,
                                                                            simplebarScroll,
                                                                            setEditingComponentID,
                                                                            isEditing,
                                                                            itemsLength
                                                                        ) => {
                                                                            return (
                                                                                <CollectionAddItemDropdown
                                                                                    customOffset={{ crossAxis: -6, mainAxis: 0 }}
                                                                                    onNew={createCollectionHandler}
                                                                                    onCollectionAddToProfile={onCollectionAddToProfile}
                                                                                    collections={privateCollections}
                                                                                    scrolling={scrolling}
                                                                                    onOpenChange={onOpenChange}
                                                                                    scrollSimpleBar={simplebarScroll}
                                                                                    isEditing={isEditing}
                                                                                >
                                                                                    <EditableIt
                                                                                        editMode
                                                                                        activeCol={""}
                                                                                        onSelectDrag={() => {}}
                                                                                        onItemSelect={() => {}}
                                                                                        onCollectionSubmitName={onCollectionSubmitName}
                                                                                        setEditingComponentID={setEditingComponentID}
                                                                                        isEditing={isEditing}
                                                                                        totalItemsNumber={itemsLength}
                                                                                        addText={
                                                                                            !privateCollections.length
                                                                                                ? "Create collection"
                                                                                                : "Add collection"
                                                                                        }
                                                                                        itemType={EditableSortableType.Collection}
                                                                                        colorVariant={AuthoryColorSchemes.userTheme}
                                                                                    />
                                                                                </CollectionAddItemDropdown>
                                                                            );
                                                                        }}
                                                                    />
                                                                </div>
                                                            )}
                                                            {showFilters && (
                                                                <div>
                                                                    <ProfileFilters
                                                                        editMode={isPrivateView}
                                                                        author={author}
                                                                        onProfileFilterEditableClick={onProfileFilterEditableClick}
                                                                        formik={formik}
                                                                        extendedMode={!isPrivateView && profileCollections.length <= 1}
                                                                    />
                                                                </div>
                                                            )}
                                                        </PCFWrapper>
                                                    </VerticalSpacing>
                                                )}
                                                {isPrivateView && isTabsCollectionLayout && collectionTarget && (
                                                    <>
                                                        {showCollectionAutoUpdateBanner(collectionTarget) && (
                                                            <CollectionBannerDetails
                                                                hasFiltersEnabled={!!collectionTarget?.ruleCount}
                                                                onCollectionUpdateRules={() =>
                                                                    onCollectionUpdateRules &&
                                                                    collectionTarget &&
                                                                    onCollectionUpdateRules(collectionTarget.canonicalSlug)
                                                                }
                                                            />
                                                        )}
                                                        {showCollectionNoNameBanner(collectionTarget) && (
                                                            <NoNameBanner entity="collection" onNameClick={() => setNameCollectionModalOpen(true)} />
                                                        )}
                                                        {!!srcInProgress.length && (
                                                            <VerticalSpacing bottom={12} top={12}>
                                                                <SourceInProgress sources={srcInProgress} allowUserThemable />
                                                            </VerticalSpacing>
                                                        )}
                                                    </>
                                                )}
                                                <VerticalSpacing top={isTabsCollectionLayout ? 24 : 0}>
                                                    {collectionIsEmpty ? (
                                                        <>
                                                            <V3ProfileEmptyCollection
                                                                isPrivate={isPrivateView}
                                                                isDefaultCollection={collectionTarget.default}
                                                            />
                                                        </>
                                                    ) : (
                                                        <>
                                                            {profileHasCollectionsAdded || isGridCollectionLayout ? (
                                                                <>
                                                                    {isTabsCollectionLayout ? (
                                                                        renderProfileList({
                                                                            items,
                                                                            smartShareUrl,
                                                                            context: ItemUrlContext.profile,
                                                                            authorSlug: author.slug,
                                                                            viewType: theme.customization.defaultView as ProfileView,
                                                                            itemsLoading,
                                                                            isPrivateView,
                                                                            pinnedItem: !!items.length ? items[0].pinned : undefined,
                                                                            collection: collectionTarget,
                                                                            signupSkeleton,
                                                                            profileStyle: theme.customization.profileStyle,
                                                                            onItemClick
                                                                        })
                                                                    ) : (
                                                                        <VerticalSpacing bottom={60}>
                                                                            {isPrivateView ? (
                                                                                <CollectionList
                                                                                    isProfilePage
                                                                                    isPrivateProfilePage={isPrivateView}
                                                                                    privateCollections={privateCollections}
                                                                                    collections={profileCollections}
                                                                                    collectionsLoading={false}
                                                                                    onCollectionAddToProfile={onCollectionAddToProfile}
                                                                                    onCollectionRemoveFromProfile={onCollectionRemoveFromProfile}
                                                                                    createCollectionHandler={createCollectionHandler}
                                                                                    onCollectionStopDrag={onCollectionStopDrag}
                                                                                    profileStyle={theme.customization.profileStyle}
                                                                                    user={author}
                                                                                    allowUserThemable
                                                                                    smartShareUrl={smartShareUrl}
                                                                                />
                                                                            ) : (
                                                                                <PublicCollectionList
                                                                                    collections={profileCollections}
                                                                                    profileStyle={theme.customization.profileStyle}
                                                                                    user={author}
                                                                                    smartShareUrl={smartShareUrl}
                                                                                />
                                                                            )}
                                                                        </VerticalSpacing>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <div style={{ minHeight: 400 }}>
                                                                    <V3NoProfileCollections
                                                                        hasCollectionToAdd={profileHasCollections}
                                                                        isPrivate={isPrivateView}
                                                                    />
                                                                </div>
                                                            )}
                                                            {!itemsLoading &&
                                                                !hasItems &&
                                                                !signupSkeleton &&
                                                                !collectionIsEmpty &&
                                                                isTabsCollectionLayout &&
                                                                profileHasCollectionsAdded && (
                                                                    <V3NoSearchResults
                                                                        onClearHandler={() => {
                                                                            formik.setFieldValue("text", DEFAULT_SEARCH_PARAMS.TEXT);
                                                                            formik.setFieldValue("type", DEFAULT_SEARCH_PARAMS.TYPE);
                                                                            formik.setFieldValue("collection", searchArgs?.collection);
                                                                            formik.setFieldValue("source", DEFAULT_SEARCH_PARAMS.SOURCE);
                                                                            formik.setFieldValue("subAccount", DEFAULT_SEARCH_PARAMS.SUBACCOUNT);
                                                                            formik.submitForm();
                                                                        }}
                                                                    />
                                                                )}
                                                        </>
                                                    )}
                                                </VerticalSpacing>
                                            </>
                                        )}
                                    </AnimationToUse>
                                </PublicProfileSearchItemsContentSizer>
                                {!isAboutPage && isTabsCollectionLayout && (
                                    <InfinitePaginationFooter
                                        contentLoading={itemsLoading}
                                        numPagesLoaded={items.length}
                                        contentCanFetchmore={contentCanFetchmore}
                                        loadMore={contentLoadmore}
                                    />
                                )}
                            </PublicProfileSearchItemsPad>
                        </MainWrapper>
                    </>
                }
            />
            {subscribeCompletedModal && (
                <V3ModalSubscribed modalOpen={subscribeCompletedModal} onCloseHandler={() => setSubscribeCompletedModal(false)} author={author} />
            )}
            {unsubscribedModal && <V3ModalUnsubscribed modalOpen={unsubscribedModal} onCloseHandler={() => setUnsubscribedModal(false)} />}
            {collectionTarget && nameCollectionModalOpen && (
                <CreateUpdateCollectionModal
                    hideDescription
                    modalOpen={nameCollectionModalOpen}
                    setModalOpen={setNameCollectionModalOpen}
                    title="Name your collection"
                    btnLabel="Save"
                    onSubmit={async (data) => {
                        if (data.name) await onCollectionSubmitName?.(data.name, collectionTarget.canonicalSlug);
                        return undefined;
                    }}
                />
            )}
            {isAboutPage && userAboutPage && nameAboutPageModalOpen && (
                <CreateUpdateCollectionModal
                    hideDescription
                    namePlaceholder="e.g. About me"
                    modalOpen={nameAboutPageModalOpen}
                    setModalOpen={setNameAboutPageModalOpen}
                    title="Name your page"
                    btnLabel="Save"
                    onSubmit={async (data) => {
                        if (data.name) await onAboutPageSubmitName?.(data.name, queryArgs.aboutPage);
                        return undefined;
                    }}
                />
            )}
        </div>
    );
};
